import { useMemo, useState } from "react";
import { Download } from "lucide-react";
import { Card, EmptyState } from "@/components/admin/AdminUI";
import { fmt, useList, COUNTRY } from "@/components/admin/finance/common";
import { PeriodFilter, periodQuery, downloadCsv } from "@/components/admin/finance/Period";

function Group({ title, rows }) {
  return (
    <Card className="overflow-hidden">
      <div className="px-4 py-3 border-b border-[#1E293B] text-sm font-semibold text-white">{title}</div>
      {rows.length === 0 ? <EmptyState text="Aucune donnée." /> : (
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="border-b border-[#1E293B] text-left text-slate-400"><th className="px-4 py-2.5">Libellé</th><th className="px-4 py-2.5 text-right">Engagé</th><th className="px-4 py-2.5 text-right">Approuvé</th><th className="px-4 py-2.5 text-right">Payé</th><th className="px-4 py-2.5 text-right">En attente</th><th className="px-4 py-2.5 text-right">En retard</th></tr></thead>
          <tbody>{rows.map((r, i) => (
            <tr key={i} className="border-b border-[#1E293B]/60">
              <td className="px-4 py-2.5 text-slate-300">{r.key} <span className="text-xs text-slate-500">({r.currency})</span></td>
              <td className="px-4 py-2.5 text-right text-white">{fmt(r.engaged, r.currency)}</td>
              <td className="px-4 py-2.5 text-right text-slate-300">{fmt(r.approved, r.currency)}</td>
              <td className="px-4 py-2.5 text-right text-emerald-400">{fmt(r.paid, r.currency)}</td>
              <td className="px-4 py-2.5 text-right text-amber-400">{fmt(r.pending, r.currency)}</td>
              <td className="px-4 py-2.5 text-right text-rose-400">{fmt(r.overdue, r.currency)}</td>
            </tr>
          ))}</tbody>
        </table></div>
      )}
    </Card>
  );
}

export default function Reports() {
  const [period, setPeriod] = useState({ from: "", to: "" });
  const pq = periodQuery(period);
  const { data } = useList(`/finance/reports/summary?country=${COUNTRY}${pq}`, [pq]);
  const suffix = period.from || period.to ? `_${period.from || "debut"}_${period.to || "fin"}` : "";
  const totals = data?.totals_by_currency || [];
  const groups = useMemo(() => ([
    ["Par catégorie", data?.by_category || []],
    ["Par fournisseur", data?.by_supplier || []],
    ["Par service / centre de coûts", data?.by_service || []],
    ["Par projet", data?.by_project || []],
    ["Par collaborateur", data?.by_collaborator || []],
  ]), [data]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <PeriodFilter value={period} onChange={setPeriod} testid="reports-period" />
        <button onClick={() => downloadCsv(`/finance/export/expenses.csv?country=${COUNTRY}${pq}`, `depenses_${COUNTRY}${suffix}.csv`)} data-testid="export-expenses-csv"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#4CB944] text-white text-sm font-semibold hover:bg-[#3AA033]">
          <Download className="w-4 h-4" /> Export comptable (CSV)
        </button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="reports-totals">
        {totals.length === 0 && <Card className="sm:col-span-4"><EmptyState text="Aucune dépense sur la période." /></Card>}
        {totals.map((t) => (
          <Card key={t.currency} className="p-5">
            <div className="text-xs text-slate-500 uppercase font-mono">Total ({t.currency}) · {t.count} dépenses</div>
            <div className="mt-2 text-2xl font-bold text-white">{fmt(t.engaged, t.currency)}</div>
            <div className="mt-3 space-y-1 text-xs">
              <div className="flex justify-between"><span className="text-slate-400">Approuvé</span><span className="text-slate-200">{fmt(t.approved, t.currency)}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Payé</span><span className="text-emerald-400">{fmt(t.paid, t.currency)}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">En attente</span><span className="text-amber-400">{fmt(t.pending, t.currency)}</span></div>
            </div>
          </Card>
        ))}
      </div>
      <div className="p-3 rounded-xl bg-blue-500/10 border border-blue-500/20 text-xs text-blue-300">
        ℹ️ Les montants ne sont jamais additionnés entre devises différentes : chaque devise est présentée séparément.
      </div>
      {groups.map(([title, rows]) => <Group key={title} title={title} rows={rows} />)}
    </div>
  );
}
